/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { CashBoxTaskStatus, CashBoxTasksListDto } from '../../../api/service/serviceClient.typings';
import {
    CashBoxTasksTabAction,
    CashBoxTasksTabActionType,
} from '../../../actions/CashBoxRegistry/Tabs/cashBoxTasksTabActions';

export const tasksListRowsPerPage = [5, 10, 15, 20];
export const defaultTasksListPaginationLimit = tasksListRowsPerPage[1];

export interface CashBoxTasksTabState {
    certUpdatesList: CashBoxTasksListDto;
    isAnyCertUpdateTaskInProgress: boolean;
    isLoadingCertUpdatesList: boolean;
    isLoadingCertUpdateRequest: boolean;

    logsList: CashBoxTasksListDto;
    isLoadingLogsList: boolean;
    isLoadingLogsRequest: boolean;
    isAnyLogsRequestTaskInProgress: boolean;

    firmwareUpdatesList: CashBoxTasksListDto;
    suitableTargetVersions: string[];
    isLoadingFirmwareUpdatesList: boolean;
    isLoadingFirmwareUpdateRequest: boolean;
    isLoadingSuitableTargetVersionsList: boolean;
    isAnyFirmwareUpdateTaskInProgress: boolean;

    envInfoList: CashBoxTasksListDto;
    isAnyEnvInfoRequestTaskInProgress: boolean;
    isLoadingEnvInfoList: boolean;
    isLoadingEnvInfoRequest: boolean;
}

const initialState: CashBoxTasksTabState = {
    certUpdatesList: null,
    isLoadingCertUpdatesList: false,
    isLoadingCertUpdateRequest: false,
    isAnyCertUpdateTaskInProgress: false,

    logsList: null,
    isLoadingLogsList: false,
    isLoadingLogsRequest: false,
    isAnyLogsRequestTaskInProgress: false,

    firmwareUpdatesList: null,
    suitableTargetVersions: null,
    isLoadingFirmwareUpdatesList: false,
    isLoadingFirmwareUpdateRequest: false,
    isLoadingSuitableTargetVersionsList: false,
    isAnyFirmwareUpdateTaskInProgress: false,

    envInfoList: null,
    isAnyEnvInfoRequestTaskInProgress: false,
    isLoadingEnvInfoList: false,
    isLoadingEnvInfoRequest: false,
};

export function cashBoxTasksTabReducer(
    state: CashBoxTasksTabState = initialState,
    action: CashBoxTasksTabAction,
): CashBoxTasksTabState {
    switch (action.type) {
        case CashBoxTasksTabActionType.OPEN_CASHBOX_TASKS_TAB:
            return {
                ...state,
            };

        case CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_START:
            return {
                ...state,
                isLoadingCertUpdatesList: true,
            };

        case CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_SUCCESS: {
            let isAnyTaskInProgress = false;
            for (let i = 0; i < action.payload.items.length; i++) {
                if (action.payload.items[i].status === CashBoxTaskStatus.CREATED) {
                    isAnyTaskInProgress = true;
                    break;
                }
            }
            return {
                ...state,
                isLoadingCertUpdatesList: false,
                certUpdatesList: action.payload,
                isAnyCertUpdateTaskInProgress: isAnyTaskInProgress,
            };
        }

        case CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_ERROR:
            return {
                ...state,
                isLoadingCertUpdatesList: false,
                certUpdatesList: {
                    count: 0,
                    items: [],
                },
            };

        case CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_START:
            return {
                ...state,
                isLoadingCertUpdateRequest: true,
            };

        case CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_SUCCESS:
            return {
                ...state,
                isLoadingCertUpdateRequest: false,
                isAnyCertUpdateTaskInProgress: true,
            };

        case CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_ERROR:
            return {
                ...state,
                isLoadingCertUpdateRequest: false,
            };

        case CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_START:
            return {
                ...state,
                isLoadingLogsList: true,
            };

        case CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_SUCCESS: {
            let isAnyTaskInProgress = false;
            for (let i = 0; i < action.payload.items.length; i++) {
                if (action.payload.items[i].status === CashBoxTaskStatus.CREATED) {
                    isAnyTaskInProgress = true;
                    break;
                }
            }
            return {
                ...state,
                isLoadingLogsList: false,
                logsList: action.payload,
                isAnyLogsRequestTaskInProgress: isAnyTaskInProgress,
            };
        }

        case CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_ERROR:
            return {
                ...state,
                isLoadingLogsList: false,
                logsList: {
                    count: 0,
                    items: [],
                },
            };

        case CashBoxTasksTabActionType.REQUEST_LOGS_START:
            return {
                ...state,
                isLoadingLogsRequest: true,
            };

        case CashBoxTasksTabActionType.REQUEST_LOGS_SUCCESS:
            return {
                ...state,
                isLoadingLogsRequest: false,
                isAnyLogsRequestTaskInProgress: true,
            };

        case CashBoxTasksTabActionType.REQUEST_LOGS_ERROR:
            return {
                ...state,
                isLoadingLogsRequest: false,
            };

        case CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_START:
            return {
                ...state,
                isLoadingLogsList: true,
            };

        case CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_SUCCESS:
        case CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_ERROR:
            return {
                ...state,
                isLoadingLogsList: false,
            };

        case CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_START:
            return {
                ...state,
                isLoadingFirmwareUpdatesList: true,
            };

        case CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_SUCCESS: {
            let isAnyTaskInProgress = false;
            for (let i = 0; i < action.payload.items.length; i++) {
                if (action.payload.items[i].status === CashBoxTaskStatus.CREATED) {
                    isAnyTaskInProgress = true;
                    break;
                }
            }
            return {
                ...state,
                isLoadingFirmwareUpdatesList: false,
                firmwareUpdatesList: action.payload,
                isAnyFirmwareUpdateTaskInProgress: isAnyTaskInProgress,
            };
        }

        case CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_ERROR:
            return {
                ...state,
                isLoadingFirmwareUpdatesList: false,
                firmwareUpdatesList: {
                    count: 0,
                    items: [],
                },
            };

        case CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_START:
            return {
                ...state,
                isLoadingFirmwareUpdateRequest: true,
            };

        case CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_SUCCESS:
            return {
                ...state,
                isLoadingFirmwareUpdateRequest: false,
                isAnyFirmwareUpdateTaskInProgress: true,
            };

        case CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_ERROR:
            return {
                ...state,
                isLoadingFirmwareUpdateRequest: false,
            };

        case CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_START:
            return {
                ...state,
                isLoadingSuitableTargetVersionsList: true,
            };

        case CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_SUCCESS:
            return {
                ...state,
                isLoadingSuitableTargetVersionsList: false,
                suitableTargetVersions: action.payload,
            };

        case CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_ERROR:
            return {
                ...state,
                isLoadingSuitableTargetVersionsList: false,
                suitableTargetVersions: null,
            };

        case CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_START:
            return {
                ...state,
                isLoadingEnvInfoList: true,
            };

        case CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_SUCCESS: {
            let isAnyTaskInProgress = false;
            for (let i = 0; i < action.payload.items.length; i++) {
                if (action.payload.items[i].status === CashBoxTaskStatus.CREATED) {
                    isAnyTaskInProgress = true;
                    break;
                }
            }
            return {
                ...state,
                isLoadingEnvInfoList: false,
                envInfoList: action.payload,
                isAnyEnvInfoRequestTaskInProgress: isAnyTaskInProgress,
            };
        }

        case CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_ERROR:
            return {
                ...state,
                isLoadingEnvInfoList: false,
                envInfoList: {
                    count: 0,
                    items: [],
                },
            };

        case CashBoxTasksTabActionType.REQUEST_ENV_INFO_START:
            return {
                ...state,
                isLoadingEnvInfoRequest: true,
            };

        case CashBoxTasksTabActionType.REQUEST_ENV_INFO_SUCCESS:
            return {
                ...state,
                isLoadingEnvInfoRequest: false,
                isAnyEnvInfoRequestTaskInProgress: true,
            };

        case CashBoxTasksTabActionType.REQUEST_ENV_INFO_ERROR:
            return {
                ...state,
                isLoadingEnvInfoRequest: false,
            };

        case CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB:
            return {
                ...state,
                certUpdatesList: null,
                isLoadingCertUpdatesList: false,
                isLoadingCertUpdateRequest: false,
                isAnyCertUpdateTaskInProgress: false,

                logsList: null,
                isLoadingLogsList: false,
                isLoadingLogsRequest: false,
                isAnyLogsRequestTaskInProgress: false,

                firmwareUpdatesList: null,
                suitableTargetVersions: null,
                isLoadingFirmwareUpdatesList: false,
                isLoadingFirmwareUpdateRequest: false,
                isLoadingSuitableTargetVersionsList: false,
                isAnyFirmwareUpdateTaskInProgress: false,

                envInfoList: null,
                isAnyEnvInfoRequestTaskInProgress: false,
                isLoadingEnvInfoList: false,
                isLoadingEnvInfoRequest: false,
            };

        default:
            break;
    }
    return state;
}
